import React, { useRef, useState, useCallback } from "react";
import Webcam from "react-webcam";

const UPLOAD_URL = "/api/upload"; // <-- change to your backend route

const videoConstraints = {
  width: 1280,
  height: 720,
  facingMode: "user",
};

const CameraRecorder: React.FC = () => {
  const webcamRef = useRef<Webcam | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);

  const [capturing, setCapturing] = useState(false);
  const [recordedChunks, setRecordedChunks] = useState<BlobPart[]>([]);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  const handleStartCapture = useCallback(() => {
    setStatus(null);
    setRecordedChunks([]);

    // react-webcam attaches the MediaStream to `webcamRef.current.stream`
    const stream = (webcamRef.current as any)?.stream as MediaStream | undefined;
    if (!stream) {
      setStatus("No webcam stream available (check camera permissions).");
      return;
    }

    let options: MediaRecorderOptions = { mimeType: "video/webm;codecs=vp9" };

    // Some browsers don't support vp9; fall back if needed
    if (!MediaRecorder.isTypeSupported(options.mimeType!)) {
      options = { mimeType: "video/webm;codecs=vp8" };
    }

    try {
      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data && event.data.size > 0) {
          setRecordedChunks((prev) => [...prev, event.data]);
        }
      };

      mediaRecorder.start();
      setCapturing(true);
      setStatus("Recording...");
    } catch (err) {
      console.error(err);
      setStatus("Error starting recording (check browser support).");
    }
  }, []);

  const handleStopCapture = useCallback(() => {
    if (!mediaRecorderRef.current) return;
    mediaRecorderRef.current.stop();
    setCapturing(false);
    setStatus("Recording stopped. Ready to upload.");
  }, []);

  const uploadBlob = useCallback(
    async (blob: Blob, fileName: string) => {
      setUploading(true);
      setStatus("Uploading...");

      try {
        const formData = new FormData();
        formData.append("file", blob, fileName);

        const response = await fetch(UPLOAD_URL, {
          method: "POST",
          body: formData,
        });

        if (!response.ok) {
          throw new Error(`Upload failed with status ${response.status}`);
        }

        setStatus("Upload successful ✅");
      } catch (err: any) {
        console.error(err);
        setStatus(`Upload failed: ${err?.message ?? "Unknown error"}`);
      } finally {
        setUploading(false);
      }
    },
    []
  );

  const handleUploadVideo = useCallback(async () => {
    if (!recordedChunks.length) {
      setStatus("No video recorded yet.");
      return;
    }

    const blob = new Blob(recordedChunks, { type: "video/webm" });
    await uploadBlob(blob, `recording-${Date.now()}.webm`);
    setRecordedChunks([]);
  }, [recordedChunks, uploadBlob]);

  const handleCapturePhoto = useCallback(async () => {
    setStatus(null);

    const imageSrc = webcamRef.current?.getScreenshot();
    if (!imageSrc) {
      setStatus("Unable to capture photo (check camera permissions).");
      return;
    }

    // dataURL -> Blob (uses fetch trick)
    const res = await fetch(imageSrc);
    const blob = await res.blob();

    await uploadBlob(blob, `photo-${Date.now()}.jpg`);
  }, [uploadBlob]);

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#0f172a",
        color: "white",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "2rem 1rem",
        fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
      }}
    >
      <h1 style={{ fontSize: "1.75rem", marginBottom: "1rem" }}>
        Camera Recorder
      </h1>

      <div
        style={{
          background: "#020617",
          borderRadius: "1rem",
          padding: "1rem",
          boxShadow: "0 20px 50px rgba(0,0,0,0.6)",
          maxWidth: "900px",
          width: "100%",
        }}
      >
        <div
          style={{
            borderRadius: "0.75rem",
            overflow: "hidden",
            background: "black",
            aspectRatio: "16 / 9",
            marginBottom: "1rem",
          }}
        >
          <Webcam
            ref={webcamRef}
            audio={true}
            mirrored={true}
            screenshotFormat="image/jpeg"
            videoConstraints={videoConstraints}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0.75rem",
            justifyContent: "center",
            marginBottom: "0.75rem",
          }}
        >
          <button
            onClick={handleStartCapture}
            disabled={capturing || uploading}
            style={{
              padding: "0.5rem 1.2rem",
              borderRadius: "999px",
              border: "none",
              cursor: capturing || uploading ? "not-allowed" : "pointer",
              fontWeight: 600,
              background: capturing ? "#4b5563" : "#22c55e",
              opacity: capturing || uploading ? 0.7 : 1,
            }}
          >
            ⏺ Start Recording
          </button>

          <button
            onClick={handleStopCapture}
            disabled={!capturing || uploading}
            style={{
              padding: "0.5rem 1.2rem",
              borderRadius: "999px",
              border: "none",
              cursor: !capturing || uploading ? "not-allowed" : "pointer",
              fontWeight: 600,
              background: !capturing ? "#4b5563" : "#ef4444",
              opacity: !capturing || uploading ? 0.7 : 1,
            }}
          >
            ⏹ Stop Recording
          </button>

          <button
            onClick={handleUploadVideo}
            disabled={capturing || uploading || !recordedChunks.length}
            style={{
              padding: "0.5rem 1.2rem",
              borderRadius: "999px",
              border: "none",
              cursor:
                capturing || uploading || !recordedChunks.length
                  ? "not-allowed"
                  : "pointer",
              fontWeight: 600,
              background:
                capturing || !recordedChunks.length ? "#4b5563" : "#3b82f6",
              opacity:
                capturing || uploading || !recordedChunks.length ? 0.7 : 1,
            }}
          >
            ⬆️ Upload Video
          </button>

          <button
            onClick={handleCapturePhoto}
            disabled={uploading}
            style={{
              padding: "0.5rem 1.2rem",
              borderRadius: "999px",
              border: "none",
              cursor: uploading ? "not-allowed" : "pointer",
              fontWeight: 600,
              background: "#eab308",
              opacity: uploading ? 0.7 : 1,
            }}
          >
            📸 Capture & Upload Photo
          </button>
        </div>

        <div
          style={{
            fontSize: "0.875rem",
            minHeight: "1.25rem",
            textAlign: "center",
            color: "#e5e7eb",
          }}
        >
          {status}
        </div>
      </div>
    </div>
  );
};

export default CameraRecorder;
